import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { operations } from '../services/api';
import QuickActions from '../components/Operations/QuickActions';
import ManualEntryDialog from '../components/Operations/ManualEntryDialog';
import WhatsAppDialog from '../components/Operations/WhatsAppDialog';
import SmsDialog from '../components/Operations/SmsDialog';
import BookClassDialog from '../components/Operations/BookClassDialog';

// Dashboard adaptativo: mostra apenas widgets/ações dos módulos ATIVOS no ginásio (decidido pelo
// backend). Nunca mostra cards vazios de módulos desligados.
//
// Quando o ginásio tem o módulo Receção ligado, este ecrã não acrescenta nada ao da Receção (os mesmos
// contadores e as mesmas ações): encaminha para lá. Fica como alternativa para ginásios sem Receção.

export default function OperationsDashboardPage() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);   // 'manual-entry' | 'whatsapp' | 'sms' | 'book-class' | null

  useEffect(() => {
    let alive = true;
    operations.dashboard().then((r) => { if (alive) { setD(r.data); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) return <div style={S.wrap}><p>A carregar…</p></div>;
  if (!d) return <div style={S.wrap}><p>Não foi possível carregar o dashboard.</p></div>;
  if (d.reception) return <Navigate to="/admin/reception" replace />;

  const w = d.widgets || {};

  return (
    <div style={S.wrap}>
      <h2 style={S.h2}>Operação</h2>

      <QuickActions
        actions={d.quickActions}
        onDialog={setDialog}
        style={S.actions}
        buttonStyle={S.action}
        disabledStyle={S.actionDisabled}
      />

      <div style={S.grid}>
        {d.accessControl && 'entriesToday' in w && <Stat label="Entradas hoje" value={w.entriesToday} />}
        {d.accessControl && 'deniedToday' in w && <Stat label="Recusas hoje" value={w.deniedToday} tone={w.deniedToday > 0 ? 'warn' : undefined} />}
        {'upcomingClasses' in w && <Stat label="Aulas próximas" value={(w.upcomingClasses || []).length} />}
        {'noShowsToday' in w && <Stat label="No-shows hoje" value={w.noShowsToday} />}
        {'newMembersToday' in w && <Stat label="Novos membros hoje" value={w.newMembersToday} />}
        {w.membershipDebt && <Stat label="Em dívida" value={`${Number(w.membershipDebt.totalOutstanding).toFixed(0)}€`} tone={w.membershipDebt.overdueCount > 0 ? 'warn' : undefined} />}
      </div>

      {d.accessControl && Array.isArray(w.recentEntries) && (
        <section style={{ marginTop: 24 }}>
          <h3 style={S.h3}>Últimas entradas</h3>
          {w.recentEntries.length === 0 ? <div style={S.empty}>Sem entradas hoje.</div> : (
            <ul style={S.list}>
              {w.recentEntries.map((e, i) => (
                <li key={i} style={S.li}>
                  <span>Membro #{e.userId ?? '—'}</span>
                  <span style={{ color: e.result === 'AUTHORIZED' ? 'var(--success-text)' : 'var(--error-text)' }}>{e.result === 'AUTHORIZED' ? 'Autorizada' : 'Recusada'} · {new Date(e.timestamp).toLocaleTimeString('pt-PT')}</span>
                </li>
              ))}
            </ul>
          )}
          {w.presenceTrackingAvailable === false && <div style={S.note}>Presença em tempo real requer eventos de saída (ainda não disponíveis).</div>}
        </section>
      )}

      {!d.accessControl && !d.reception && (
        <div style={S.empty}>Controlo de acessos e receção desligados. Foca em agenda, membros e financeiro conforme os módulos ativos.</div>
      )}

      <ManualEntryDialog open={dialog === 'manual-entry'} onClose={() => setDialog(null)} />
      <WhatsAppDialog open={dialog === 'whatsapp'} onClose={() => setDialog(null)} />
      <SmsDialog open={dialog === 'sms'} onClose={() => setDialog(null)} />
      <BookClassDialog open={dialog === 'book-class'} onClose={() => setDialog(null)} />
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div style={{ ...S.stat, borderColor: tone === 'warn' ? 'var(--warning)' : 'var(--border-color)' }}>
      <div style={{ ...S.statValue, color: tone === 'warn' ? 'var(--warning-text)' : 'var(--text-primary)' }}>{value}</div>
      <div style={S.statLabel}>{label}</div>
    </div>
  );
}

const S = {
  wrap: { maxWidth: 960, margin: '0 auto', padding: 16 },
  h2: { fontSize: 22, fontWeight: 700 },
  h3: { fontSize: 15, fontWeight: 700, marginBottom: 8 },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 },
  action: { padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--accent-primary)', color: 'var(--text-inverse)', cursor: 'pointer', fontSize: 14 },
  actionDisabled: { background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12, marginTop: 20 },
  stat: { border: '1px solid var(--border-color)', borderRadius: 12, padding: 16 },
  statValue: { fontSize: 26, fontWeight: 700 },
  statLabel: { color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 },
  list: { listStyle: 'none', padding: 0, margin: 0 },
  li: { display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border-color)', fontSize: 14 },
  empty: { color: 'var(--text-muted)', fontSize: 14, padding: 16, marginTop: 12 },
  note: { color: 'var(--text-muted)', fontSize: 12, marginTop: 8 },
};
